'use client';

import type React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useConsent } from '../../contexts/ConsentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { StyledCaption } from '../styled';

const FooterWrapper = styled.footer`
  width: 100%;
  padding: 40px 20px;
  background: var(--bg-primary, #05050a);
  border-top: 1px solid var(--card-border, rgba(255, 255, 255, 0.08));
  text-align: center;
  position: relative;
  z-index: 20;
  transition: background-color 0.3s ease;
`;

const LegalLinks = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const LegalAnchor = styled(Link)`
  color: var(--text-secondary, rgba(255, 255, 255, 0.65));
  font-size: 0.8rem;
  font-variant-caps: all-petite-caps;
  text-decoration: none;
  letter-spacing: 1px;
  transition: color 0.2s;

  &:hover {
    color: #00ff00;
    text-decoration: underline;
  }
`;

const SettingsButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary, rgba(255, 255, 255, 0.65));
  font-size: 0.8rem;
  font-variant-caps: all-petite-caps;
  letter-spacing: 1px;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s;

  &:hover {
    color: #00ff00;
    text-decoration: underline;
  }
`;

const ExtLink = styled.a`
  color: #ff2d95;
  text-decoration: underline;
  font-weight: 500;

  &:hover {
    color: #00ff00;
  }
`;

/**
 * Global site footer with dynamic copyright, legal links, and privacy settings trigger.
 */
export const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { openSettings } = useConsent();
  const { t, language } = useLanguage();

  return (
    <FooterWrapper>
      <StyledCaption
        style={{
          color: '#f0f',
          opacity: 0.9,
          fontSize: '1rem',
        }}
      >
        © 2012–{currentYear} {language === 'de' ? 'von ' : 'by '}
        <ExtLink href="https://fabianalthaus.de" target="_blank" rel="noopener noreferrer">
          Fabian Althaus
        </ExtLink>
        {language === 'de'
          ? ', alias Joev Noise. Alle Rechte vorbehalten.'
          : ', alias Joev Noise. All rights reserved.'}
      </StyledCaption>

      <LegalLinks>
        <LegalAnchor to="/imprint">{t('footerImprint')}</LegalAnchor>
        <LegalAnchor to="/privacy">{t('footerPrivacy')}</LegalAnchor>
        <SettingsButton onClick={openSettings} aria-label={t('footerPrivacySettings')}>
          {t('footerPrivacySettings')}
        </SettingsButton>
      </LegalLinks>
    </FooterWrapper>
  );
};

export default Footer;
